import { roundMoney } from './quote-totals';

export function assertExportItemId(value: unknown, path: string): asserts value is number {
  if (!Number.isSafeInteger(value) || (value as number) <= 0) {
    throw new TypeError(`${path} muss eine definierte, endliche sichere Ganzzahl sein (größer 0)`);
  }
}

export function assertExportOptionalBoolean(
  value: unknown,
  path: string,
): asserts value is boolean | undefined {
  if (value !== undefined && typeof value !== 'boolean') {
    throw new TypeError(`${path} muss ein Boolean sein`);
  }
}

/**
 * `requirePositive` (B-07): Eine ausgeschriebene Leistungsposition ohne Menge ist
 * fachlich sinnlos — der Bieter kann sie nicht bepreisen. Für solche Zeilen muss
 * die Menge echt grösser 0 sein, und zwar AUCH dann, wenn sie ganz fehlt: der
 * `optional`-Zweig darf eine fehlende Menge dann nicht mehr still zu 0 machen.
 *
 * Ohne `requirePositive` bleibt das Verhalten unverändert (0 ist erlaubt) — der
 * JSON-Export eines bestehenden Angebots darf eine 0-Menge weiterhin abbilden.
 */
export function parseExportNonNegativeNumber(
  value: unknown,
  path: string,
  options: {
    optional?: boolean;
    allowNumericString?: boolean;
    requirePositive?: boolean;
  } = {},
): number {
  const requirement = options.requirePositive
    ? 'muss eine endliche Zahl größer 0 sein'
    : 'muss eine endliche, nichtnegative Zahl sein';

  if ((value === undefined || value === null) && options.optional) {
    if (options.requirePositive) {
      throw new TypeError(`${path} ${requirement}`);
    }
    return 0;
  }

  let parsed: number;
  if (typeof value === 'number') {
    parsed = value;
  } else if (
    options.allowNumericString
    && typeof value === 'string'
    && /^\s*(?:\d+(?:[.,]\d*)?|[.,]\d+)\s*$/.test(value)
  ) {
    parsed = Number(value.trim().replace(',', '.'));
  } else {
    throw new TypeError(`${path} ${requirement}`);
  }

  if (
    !Number.isFinite(parsed)
    || parsed < 0
    || (options.requirePositive && parsed <= 0)
  ) {
    throw new TypeError(`${path} ${requirement}`);
  }
  return parsed;
}

export function assertExportVat(value: unknown, path: string): asserts value is 0 | 7 | 19 {
  if (value !== 0 && value !== 7 && value !== 19) {
    throw new TypeError(`${path} muss 0, 7 oder 19 sein`);
  }
}

export function normalizeExportDiscountValue(
  type: 'percent' | 'absolute',
  rawValue: number,
  baseNet: number,
): number {
  const nonNegative = Math.max(0, rawValue);
  return type === 'absolute'
    ? roundMoney(Math.min(nonNegative, baseNet))
    : Math.min(100, nonNegative);
}
